import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, first, Observable } from 'rxjs';

import { countedShareReplay } from '@blaze-shared/shared-util';

/**
 * Service is used to inform that a layout is being rendered
 * and certain engine runtime features should be locked and wait for the rendering to finish.
 *
 * Service that allows to lock the data flow within the layout
 * whenever the layout gets loaded or changes for better performance.
 * This way the process of rendering various parts of the layout
 * can be performed in steps without locking the main thread of the browser for a long time.
 *
 * Such lock is needed to avoid race conditions depending on the layout structure.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutLockService {
  private lockSet: Set<string> = new Set<string>();
  private lockSubject$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  readonly lock$ = this.lockSubject$$.pipe(
    // Lock only when the layout is locked and the runtime is not running
    distinctUntilChanged(),
    countedShareReplay(),
  );

  /** Emits a single boolean value when unlocked and completes */
  get unlocked$(): Observable<boolean> {
    return this.lock$.pipe(first((lockState) => !lockState));
  }

  setLock(lockId: string): void {
    this.lockSet.add(lockId);
    this.updateLockSubject();
  }

  removeLock(lockId: string): void {
    this.lockSet.delete(lockId);
    this.updateLockSubject();
  }

  private updateLockSubject(): void {
    const hasActiveLock = this.lockSet.size > 0;
    this.lockSubject$$.next(hasActiveLock);
  }
}
